/**
 * CustomWidget — kafel pulpitu renderujący definicję widgetu danych z kreatora (Faza 23, T1.8).
 *
 * Dane liczy backend z ZAPISANEJ definicji po `config.definitionId` (spec nigdy z klienta). Fetch +
 * zakres firmy + obsługa stanów HTTP/loading żyją TUTAJ (specyficzne dla hosta pulpitu); czysty
 * dispatch WYNIKU (`value.kind` → KPI/wykres/tabela, `ok:false` → łagodny stan) jest wydzielony do
 * `WidgetResultView` (Etap 3 T2) — współdzielony z klockiem `widget_view` na formatce kartoteki,
 * żeby oba hosty nie duplikowały tego samego dispatchu.
 *
 * MIKRO-dispatch wykresu (słupki/kołowy/donut/linia) po ŻYWYM `visualType` z odpowiedzi endpointu
 * danych (`WidgetDataApiResponse`) — snapshot `config.visualType` zapisany przy DODANIU widgetu do
 * pulpitu zostaje tylko jako fallback na czas przed pierwszym załadowaniem danych (i dla starych
 * kafli sprzed tej zmiany); po zafetchowaniu żywa wartość zawsze wygrywa, więc zmiana typu
 * wizualnego w definicji odzwierciedla się na pulpicie bez usuwania/dodawania kafla na nowo.
 *
 * Błąd per-widget wraca w treści 200 jako `{ok:false, failure}` (nie status HTTP) — mapujemy go na
 * łagodny stan (jak KpiWidget/ChartWidget przy 400/403). Ostateczny wygląd karty przy braku
 * uprawnień/błędzie (kłódka vs ukrycie) to decyzja UX (T1.9); dziś trzymamy spójny łagodny stan.
 */

import { useTranslation } from 'react-i18next';

import { useWidgetData } from '@/hooks/useWidgetDefinitions';
import { useActiveCompanyScope } from '@/lib/use-active-company';

import { classifyDataError } from '../dashboard-api';
import type { WidgetRenderProps } from '../types';

import { WidgetResultView } from './WidgetResultView';
import { WidgetDataError, WidgetEmpty, WidgetLoading } from './WidgetState';

export function CustomWidget({ widget }: WidgetRenderProps) {
  const { t } = useTranslation();
  const { isAll, companyId } = useActiveCompanyScope();

  const definitionId = widget.type === 'custom' ? widget.config.definitionId : '';
  const visualSnapshot = widget.type === 'custom' ? widget.config.visualType : undefined;

  const query = useWidgetData(
    definitionId,
    undefined,
    // scope w kluczu: przełączenie firmy odświeża kafel (X-Company-Id dopina interceptor)
    { enabled: !!definitionId, companyScope: isAll ? 'all' : companyId },
  );

  if (widget.type !== 'custom') return null;
  if (!definitionId) return <WidgetEmpty message={t('tenant.widgets.host.missingDefinition')} />;
  if (query.isLoading) return <WidgetLoading />;
  if (query.isError) {
    return (
      <WidgetDataError kind={classifyDataError(query.error)} onRetry={() => query.refetch()} />
    );
  }

  const result = query.data;
  if (!result) return <WidgetLoading />;
  return <WidgetResultView result={result} visualType={result.visualType ?? visualSnapshot} />;
}
